<template>
  <div class="relative min-h-screen overflow-y-auto flex flex-col items-center justify-start pb-10 px-4">
    <div class="fixed inset-0 bg-gray-900" style="z-index: -40;"></div>
    <div class="fixed inset-0 -z-30">
      <img src="/assets/photo.jpg" alt="" class="w-full h-full object-cover opacity-70 bg-image-animate" />
    </div>
    <div class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/60 via-blue-900/60 to-gray-900/60 mix-blend-multiply"></div>
    <div class="fixed inset-0 -z-10 bg-black/20 backdrop-blur-[2px]"></div>

    <div class="z-10 max-w-3xl mx-auto w-full space-y-6 pt-10">
      <!-- Hero -->
      <div class="p-6 sm:p-10 text-center backdrop-blur-sm bg-black/20 rounded-3xl border border-white/10 shadow-2xl">
        <div class="flex items-center justify-center gap-3 sm:gap-4 mb-6 min-w-0">
          <img class="h-10 sm:h-14 flex-shrink-0 drop-shadow-lg" :src="logoURLWH" alt="Engagr" />
          <template v-if="dealerName">
            <div class="h-8 sm:h-10 w-px bg-white/30"></div>
            <span class="text-white font-medium text-base sm:text-xl md:text-2xl truncate">{{ dealerName }}</span>
          </template>
        </div>

        <div class="mb-3 inline-flex mx-auto">
          <div class="h-1.5 w-1.5 rounded-full bg-blue-400 animate-ping mr-1"></div>
          <div class="h-1.5 w-1.5 rounded-full bg-blue-400 animate-ping mr-1" style="animation-delay: 300ms"></div>
          <div class="h-1.5 w-1.5 rounded-full bg-blue-400 animate-ping" style="animation-delay: 600ms"></div>
        </div>

        <p class="hero-headline text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent drop-shadow-lg mb-3 px-2 leading-tight">
          Reconnect with your customers.
        </p>
        <p class="text-base sm:text-lg text-blue-200 font-light italic mb-6 px-2">
          <span v-if="dealerName">{{ dealerName }}'s</span><span v-else>This</span> Engagr account is paused — switch it back on anytime.
        </p>

        <!-- Recent delivery photos (auto-scrolling marquee) -->
        <div v-if="photos.length > 0" class="photo-marquee -mx-6 sm:-mx-10 mb-6 overflow-hidden">
          <div class="photo-marquee-track flex gap-3 sm:gap-4 w-max">
            <div v-for="(photo, idx) in marqueePhotos" :key="idx"
              class="flex-shrink-0 rounded-2xl overflow-hidden border border-white/20 shadow-lg bg-black/30">
              <img :src="photo.url" :alt="photo.name || 'Customer delivery'"
                referrerpolicy="no-referrer"
                class="block w-40 h-40 sm:w-52 sm:h-52 object-cover" />
            </div>
          </div>
        </div>

        <!-- Value props -->
        <div class="flex flex-col gap-3 sm:gap-4 text-left mt-8 max-w-3xl mx-auto">
          <div v-for="prop in valueProps" :key="prop.title"
            class="border rounded-2xl p-4 sm:p-5 backdrop-blur-sm shadow-lg"
            :class="prop.cardClass">
            <div class="flex items-center gap-4 sm:gap-5">
              <div class="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shadow-lg border"
                :class="prop.iconClass">
                <span class="text-xl" v-html="prop.icon"></span>
              </div>
              <div class="min-w-0 flex-1">
                <h4 class="text-base sm:text-lg font-semibold text-white mb-0.5 sm:mb-1">{{ prop.title }}</h4>
                <p class="text-sm sm:text-base text-blue-100/85 leading-snug">{{ prop.body }}</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Request form -->
      <div class="p-6 sm:p-8 backdrop-blur-sm bg-black/20 rounded-3xl border border-white/10 shadow-2xl">
        <div v-if="sent" class="text-center py-4">
          <div class="mx-auto w-14 h-14 bg-green-500/20 border border-green-400/30 rounded-full flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-white mb-2">Thanks — we'll be in touch shortly</h3>
          <p class="text-sm text-blue-100/80">A member of our team will reach out within one business day to walk you through reactivation.</p>
        </div>

        <form v-else @submit.prevent="submit" class="space-y-4">
          <div class="text-center mb-6">
            <p class="hero-headline text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent drop-shadow-lg mb-3 px-2 leading-tight">
              Request Reactivation
            </p>
            <p class="text-base sm:text-lg text-blue-200 font-light italic px-2 mb-5">Every delivery without Engagr is a missed referral. Let's get you reconnected today.</p>

            <div class="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 py-2.5 rounded-full bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border border-amber-400/40 shadow-lg">
              <span class="text-base sm:text-lg font-semibold uppercase tracking-wider text-amber-200">★ Welcome back offer</span>
              <span class="hidden sm:inline text-amber-300/50">|</span>
              <span class="text-xs sm:text-sm text-amber-100">First month free when you reactivate this week<span class="text-amber-300">*</span></span>
            </div>
          </div>

          <div class="hp-field" aria-hidden="true">
            <label>Website</label>
            <input v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
          </div>

          <div class="grid sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-blue-100 mb-1.5">Your name</label>
              <input v-model="form.name" type="text" required
                class="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent" />
            </div>
            <div>
              <label class="block text-xs font-medium text-blue-100 mb-1.5">Email</label>
              <input v-model="form.email" type="email" required
                class="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent" />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-blue-100 mb-1.5">Message <span class="text-blue-200/50 font-normal">(optional)</span></label>
            <textarea v-model="form.message" rows="3"
              placeholder="Tell us how we can help get you back up and running…"
              class="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"></textarea>
          </div>

          <div v-if="error" class="text-red-300 text-sm bg-red-500/10 border border-red-400/30 rounded-lg px-3 py-2">{{ error }}</div>

          <button type="submit" :disabled="submitting"
            class="w-full inline-flex justify-center items-center gap-4 py-3 px-8 rounded-full transition-all duration-300 shadow-lg"
            :class="submitting ? 'bg-blue-500/60 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'">
            <svg v-if="submitting"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              class="flex-shrink-0 h-10 w-10 text-white animate-spin">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" stroke="currentColor" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <svg v-else
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="flex-shrink-0 h-10 w-10 text-white">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
            </svg>
            <span class="text-md font-semibold text-white">{{ submitting ? 'Sending…' : 'Request Reactivation' }}</span>
          </button>

          <p class="text-center text-[11px] text-blue-200/60 -mt-1">
            <span class="text-amber-300/80">*</span> Terms &amp; conditions apply. Offer subject to eligibility and account review.
          </p>
        </form>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AccountDeactivated',
  props: {
    slug: { type: String, default: '' },
    dealerName: { type: String, default: '' }
  },
  data() {
    return {
      form: { name: '', email: '', message: '', website: '' },
      submitting: false,
      sent: false,
      error: null,
      photos: [],
      logoURLWH: '/assets/engagr-wh.svg',
      valueProps: [
        {
          title: 'Spotlight happy customers',
          body: 'Capture the delivery moment and turn every handover into a shareable, branded story.',
          icon: '★',
          cardClass: 'bg-gradient-to-br from-amber-500/15 to-orange-500/5 border-amber-400/30',
          iconClass: 'bg-gradient-to-br from-amber-400/40 to-orange-500/40 border-amber-300/40 text-amber-100'
        },
        {
          title: 'Automated engagement',
          body: 'Welcome emails & SMS that drive repeat business, 5-star reviews and referrals — on autopilot.',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg>',
          cardClass: 'bg-gradient-to-br from-emerald-500/15 to-green-500/5 border-emerald-400/30',
          iconClass: 'bg-gradient-to-br from-emerald-400/40 to-green-500/40 border-emerald-300/40 text-emerald-100'
        },
        {
          title: 'Engagement Trends',
          body: 'Yearly submissions (all time) plus live tracking of sends, opens, clicks and salesperson performance.',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>',
          cardClass: 'bg-gradient-to-br from-blue-500/15 to-indigo-500/5 border-blue-400/30',
          iconClass: 'bg-gradient-to-br from-blue-400/40 to-indigo-500/40 border-blue-300/40 text-blue-100'
        },
        {
          title: 'Pick up where you left off',
          body: 'Your templates, history and team are still here. Reactivate in 24 hours — no setup required.',
          icon: '↻',
          cardClass: 'bg-gradient-to-br from-purple-500/15 to-pink-500/5 border-purple-400/30',
          iconClass: 'bg-gradient-to-br from-purple-400/40 to-pink-500/40 border-purple-300/40 text-purple-100'
        }
      ]
    };
  },
  computed: {
    // Duplicate the list so the CSS marquee animation can loop seamlessly:
    // it animates translateX from 0 to -50% of the track's width, and the
    // second copy fills the gap as the first scrolls off.
    marqueePhotos() {
      if (!this.photos.length) return [];
      return [...this.photos, ...this.photos];
    }
  },
  mounted() {
    this.fetchRecentPhotos();
  },
  watch: {
    slug() {
      this.photos = [];
      this.fetchRecentPhotos();
    }
  },
  methods: {
    async fetchRecentPhotos() {
      if (!this.slug) return;
      try {
        const { data } = await axios.get(
          `/.netlify/functions/recent-delivery-photos?slug=${encodeURIComponent(this.slug)}&limit=12`,
          { timeout: 6000 }
        );
        const raw = Array.isArray(data && data.photos)
          ? data.photos.filter(p => p && typeof p.url === 'string')
          : [];
        // Pre-validate each URL by loading it. Only photos that actually
        // render get committed to `photos`. Removing failed items after
        // they're in the DOM mid-animation causes the marquee track width
        // to jump and a blank slot to appear at the loop seam.
        const loaded = await Promise.all(raw.map(p => this.preloadPhoto(p)));
        this.photos = loaded.filter(Boolean);
      } catch (e) {
        // Silent fail: carousel is supplementary, never blocks the takeover.
      }
    },
    preloadPhoto(photo) {
      return new Promise((resolve) => {
        const img = new Image();
        img.referrerPolicy = 'no-referrer';
        img.onload = () => resolve(photo);
        img.onerror = () => resolve(null);
        img.src = photo.url;
      });
    },
    async submit() {
      this.submitting = true;
      this.error = null;
      try {
        await axios.post('/.netlify/functions/request-reactivation', {
          slug: this.slug,
          dealerName: this.dealerName,
          name: this.form.name,
          email: this.form.email,
          message: this.form.message,
          website: this.form.website,
          pageUrl: typeof window !== 'undefined' ? window.location.href : ''
        });
        this.sent = true;
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not send request. Please try again or email us directly.';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}
@keyframes subtle-zoom {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.hero-headline {
  -webkit-background-clip: text;
  background-clip: text;
  display: block;
  line-height: 1.1;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.hp-field {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.photo-marquee {
  /* Fade out the edges so photos don't visually slam into the card border. */
  mask-image: linear-gradient(to right, transparent, black 6%, black 94%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 6%, black 94%, transparent);
}
.photo-marquee-track {
  animation: photo-marquee-scroll 28s linear infinite;
  will-change: transform;
}
.photo-marquee:hover .photo-marquee-track {
  animation-play-state: paused;
}
@keyframes photo-marquee-scroll {
  from { transform: translateX(0); }
  /* The track contains two copies of the photo list; -50% lands the
     second copy exactly where the first started, giving a seamless loop. */
  to { transform: translateX(-50%); }
}
@media (prefers-reduced-motion: reduce) {
  .photo-marquee-track {
    animation: none;
  }
}
</style>
